// SENDEKØ — meldinger som IKKE fikk gå ut, holdt til de kan.
//
// ── ⚠ FEILEN DENNE FILA RETTER ──────────────────────────────────────────────
// `LiveFeed.send()` gjorde dette:
//
//     if (this.ws?.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify(msg));
//
// Er ikke socketen åpen, forsvinner meldingen. Ingen kø, ingen feil, ingen
// beskjed. En operatør som sender en CONTACT i det øyeblikket WebSocket-en
// kobler til igjen — som skjer HELE TIDEN på mobilt nett — får «✓ Sendt» og har
// ingen melding. Det er nøyaktig samme feilklasse som samband-utboksen i hubben
// (bølge 210): en destruktiv/tapende sendevei med svelget feil.
//
// UX-gjennomgangen 2026-08-18 traff dette som funn **F7**:
//   «Brukeren vet ofte ikke om meldingen faktisk kom fram. I strid er usikkerhet
//   om levering det motsatte av godfølelse. Konsekvens: folk dobbeltmelder på
//   radio for sikkerhets skyld — da har systemet tapt.»
// Rapportens Fase 3 ber om «robust leverings-/relemodell for dårlig samband
// (offline-kø)». Dette er den køen.
//
// ── ⚠ IKKE ALT SKAL KØES, OG DET ER EN SIKKERHETSEGENSKAP ───────────────────
// En musepeker-posisjon fra fire minutter siden er ikke en forsinket melding —
// den er FEIL informasjon. Å spille den av ved gjenoppkobling ville flyttet en
// annen operatørs peker til der noen sto for fire minutter siden. Samme for
// tegning og enhetsliste: de sendes som HELE SNAPSHOT, så en gammel kopi ville
// RULLET TILBAKE nyere arbeid.
//
// Derfor tre politikker per meldingstype, eksplisitt satt (se `KO_POLITIKK`):
//   • `droppes` — efemert. Køes ikke. Tapet er riktig.
//   • `siste`   — snapshot. Bare nyeste holdes; en gammel overskriver ikke.
//   • `alle`    — egen hendelse. Holdes, men dedupliseres på objekt-id, for to
//                 versjoner av samme rapport er ikke to rapporter.
//
// Rene funksjoner + injisert lagring, så alt kan testes i vitest uten DOM eller
// localStorage (apps/cop har ingen jsdom — se VALE-kort SENS-30).

/** Meldingstypene `LiveFeed.send` tar imot. Speiler unionen i `feed.ts`. */
export type SendeType =
  | 'pointer' | 'draw' | 'units' | 'report' | 'intel' | 'field' | 'chat' | 'rfi'
  | 'casualty' | 'logstat' | 'resupply' | 'checklist' | 'eso' | 'calendar'
  | 'emergency' | 'orbat' | 'myndighet' | 'teig' | 'teig-slett' | 'rfi-svar'
  | 'kalender-slett';

export type KoPolitikk = 'droppes' | 'siste' | 'alle';

/**
 * Hva som skjer med hver meldingstype når sambandet er nede.
 *
 * ⚠ EKSPLISITT PER TYPE, IKKE EN DEFAULT. En ny meldingstype som glapp inn i en
 * `default: 'alle'` kunne blitt køet og spilt av som et gammelt snapshot — altså
 * rullet tilbake nyere data uten at noen skjønte hvorfor. `politikkFor()` sier
 * derfor `droppes` for ukjente typer og LOGGER det: å miste en ny meldingstype
 * er en feil noen må rette, men å RULLE TILBAKE situasjonsbildet er verre.
 */
export const KO_POLITIKK: Record<SendeType, KoPolitikk> = {
  // Efemert: verdiløst eller direkte feil etter noen sekunder.
  pointer: 'droppes',
  // Snapshot: hele settet sendes hver gang, så bare det nyeste er sant.
  draw: 'siste',
  units: 'siste',
  orbat: 'siste',
  // Egne hendelser. Hver av dem er noe et menneske har bestemt eller observert,
  // og ingen av dem blir usanne av å komme fram to minutter senere.
  report: 'alle',
  intel: 'alle',
  field: 'alle',
  chat: 'alle',
  rfi: 'alle',
  'rfi-svar': 'alle',
  casualty: 'alle',
  logstat: 'alle',
  resupply: 'alle',
  checklist: 'alle',
  eso: 'alle',
  calendar: 'alle',
  'kalender-slett': 'alle',
  // ⚠ NØDALARM KØES. En 112 som ikke gikk ut fordi nettet blinket skal gå ut i
  // det sekundet nettet er tilbake — ikke være tapt fordi den var «efemer».
  emergency: 'alle',
  myndighet: 'alle',
  teig: 'alle',
  'teig-slett': 'alle',
};

export function politikkFor(type: string): KoPolitikk {
  return (KO_POLITIKK as Record<string, KoPolitikk | undefined>)[type] ?? 'droppes';
}

/** Én køet melding. `nokkel` styrer dedupliseringen. */
export interface KoPost {
  /** Stabil dedup-nøkkel. To poster med samme nøkkel er samme sak. */
  nokkel: string;
  /** Når meldingen ble FORSØKT sendt (ikke når den ble spilt av). */
  tsMs: number;
  /** Meldingen, ordrett slik `send()` fikk den. */
  msg: Record<string, unknown>;
}

/**
 * Maks poster i køen.
 *
 * 500 er valgt høyt med vilje: en avdeling uten samband i en time produserer
 * langt mindre enn dette, og køen er den ENESTE kopien av det operatøren har
 * skrevet. Nås taket, dropper vi de ELDSTE — men `overlop` teller dem, og flata
 * SKAL si tallet. En stille kappet kø er den samme løgnen fila retter.
 */
export const MAKS_KO = 500;

/** Nøkkelen i lagringen. Egen per bruker settes av kalleren via `prefiks`. */
export const KO_LAGERNOKKEL = 'skade.sendeko';

/**
 * Objekt-id-en i en melding, om den har en.
 *
 * Brukes til dedup: to versjoner av samme rapport er ikke to rapporter, og den
 * eldste er strengt foreldet (`mergeReport` i hubben ville forkastet den
 * uansett). Uten dette kunne en operatør som retter en melding fem ganger under
 * nedetid fått fem kopier ut ved gjenoppkobling.
 */
export function objektId(msg: Record<string, unknown>): string | null {
  const type = String(msg.type ?? '');
  const nyttelast = msg[type];
  if (nyttelast && typeof nyttelast === 'object') {
    const id = (nyttelast as { id?: unknown }).id;
    if (typeof id === 'string' && id) return id;
  }
  // Slettemeldinger bærer id-en direkte (`teig-slett`, `kalender-slett`).
  if (typeof msg.id === 'string' && msg.id) return msg.id;
  return null;
}

/**
 * Dedup-nøkkelen for en melding.
 *
 * `siste`-typer får typenavnet alene (ett spor per type — nyeste snapshot vinner).
 * `alle`-typer får `type:id` når det finnes en id, ellers en unik nøkkel, for to
 * feltmeldinger uten id ER to feltmeldinger.
 */
export function koNokkel(msg: Record<string, unknown>, unik: () => string): string {
  const type = String(msg.type ?? '');
  if (politikkFor(type) === 'siste') return type;
  const id = objektId(msg);
  return id ? `${type}:${id}` : `${type}:${unik()}`;
}

export interface KoStatus {
  /** Hvor mange som venter. */
  ventende: number;
  /** Tidsstempel på den eldste ventende (ms), eller null. */
  eldsteMs: number | null;
  /** Hvor mange som er droppet på grunn av overløp, i denne sesjonen. */
  overlop: number;
}

export interface KoLager {
  hent(nokkel: string): string | null;
  sett(nokkel: string, verdi: string): void;
}

/**
 * Sendekøen.
 *
 * ⚠ SKRIVER TIL DISK (localStorage) VED HVER ENDRING. Grunnen er konkret: uten
 * det ville en operatør som trykker F5 — eller hvis fanen krasjer — mistet alt
 * hun skrev mens sambandet var nede, og det er nøyaktig det tilfellet køen
 * finnes for. Feiler skrivingen (privat modus, full kvote), går køen videre i
 * minnet framfor å kaste meldingen; `lagerFeil` sier at holdbarheten er borte.
 */
export class Sendeko {
  private poster: KoPost[] = [];
  private overlopTeller = 0;
  private seq = 0;
  /** Satt hvis lagringen ikke virker — flata SKAL kunne si det. */
  lagerFeil: string | null = null;

  constructor(
    private lager: KoLager | null,
    private nokkel = KO_LAGERNOKKEL,
  ) {
    this.les();
  }

  /**
   * Legg en melding i køen. Returnerer `false` når typen ikke skal køes.
   *
   * ⚠ RETURVERDIEN ER IKKE «DET GIKK GALT». `false` for en `pointer` betyr at
   * tapet er tilsiktet. Kalleren skal ikke varsle operatøren om det.
   */
  leggIKo(msg: Record<string, unknown>, naaMs: number): boolean {
    const type = String(msg.type ?? '');
    if (politikkFor(type) === 'droppes') return false;
    const nokkel = koNokkel(msg, () => `${naaMs}-${this.seq++}`);
    const eksisterende = this.poster.findIndex((p) => p.nokkel === nokkel);
    const post: KoPost = { nokkel, tsMs: naaMs, msg };
    if (eksisterende >= 0) {
      // ⚠ BEHOLDER DEN OPPRINNELIGE TIDA. Alderen på køposten skal si hvor lenge
      // operatøren har ventet på å få meldingen ut, ikke når hun sist rettet et
      // felt. Ellers ser en melding som har hengt i tjue minutter fersk ut.
      post.tsMs = this.poster[eksisterende]!.tsMs;
      this.poster[eksisterende] = post;
    } else {
      this.poster.push(post);
    }
    if (this.poster.length > MAKS_KO) {
      const fjernet = this.poster.length - MAKS_KO;
      this.poster.splice(0, fjernet);
      this.overlopTeller += fjernet;
    }
    this.skriv();
    return true;
  }

  /**
   * Prøv å sende alt. `sender` returnerer `true` når meldingen faktisk gikk ut.
   *
   * ⚠ STOPPER VED FØRSTE FEIL. Går én melding ikke ut, er sambandet nede, og
   * resten skal ikke brukes til å finne det ut på nytt — rekkefølgen er dessuten
   * meningsfull (en rettelse skal ikke ankomme før meldingen den retter).
   */
  toem(sender: (msg: Record<string, unknown>) => boolean): number {
    let sendt = 0;
    while (this.poster.length) {
      const p = this.poster[0]!;
      let ok = false;
      try { ok = sender(p.msg); } catch { ok = false; }
      if (!ok) break;
      this.poster.shift();
      sendt++;
    }
    if (sendt) this.skriv();
    return sendt;
  }

  status(): KoStatus {
    return {
      ventende: this.poster.length,
      eldsteMs: this.poster.length ? this.poster[0]!.tsMs : null,
      overlop: this.overlopTeller,
    };
  }

  /** Til test og diagnose. Kopi, så ingen kan mutere køen utenfra. */
  snapshot(): KoPost[] { return this.poster.map((p) => ({ ...p })); }

  private les(): void {
    if (!this.lager) return;
    let raa: string | null = null;
    try { raa = this.lager.hent(this.nokkel); } catch (e) {
      this.lagerFeil = e instanceof Error ? e.message : String(e);
      return;
    }
    if (!raa) return;
    try {
      const inn = JSON.parse(raa) as unknown;
      if (!Array.isArray(inn)) return;
      // ⚠ VALIDERER HVER POST. En halvskrevet fil (fanen døde midt i en skriving)
      // skal gi en delvis kø, ikke en kø der én ødelagt post velter avspillingen.
      for (const p of inn.slice(0, MAKS_KO)) {
        if (!p || typeof p !== 'object') continue;
        const { nokkel, tsMs, msg } = p as Partial<KoPost>;
        if (typeof nokkel !== 'string' || typeof tsMs !== 'number') continue;
        if (!msg || typeof msg !== 'object') continue;
        this.poster.push({ nokkel, tsMs, msg: msg as Record<string, unknown> });
      }
    } catch {
      // Ulesbar fil: start tomt. ⚠ IKKE et unntak videre — en ødelagt kø skal
      // ikke hindre operatøren fra å bruke systemet.
      this.poster = [];
    }
  }

  private skriv(): void {
    if (!this.lager) return;
    try {
      this.lager.sett(this.nokkel, JSON.stringify(this.poster));
      this.lagerFeil = null;
    } catch (e) {
      this.lagerFeil = e instanceof Error ? e.message : String(e);
    }
  }
}

/**
 * Linja operatøren leser.
 *
 * ⚠ SIER ALLTID ALDEREN PÅ DEN ELDSTE. «3 meldinger venter» kan være tre
 * sekunder eller tjue minutter, og de to betyr helt ulike ting: det første er
 * normal drift, det andre er at hun bør vurdere radio. Uten alderen må hun gjette.
 */
export function koTekst(st: KoStatus, naaMs: number): string {
  const deler: string[] = [];
  if (st.ventende) {
    const s = Math.max(0, Math.floor((naaMs - (st.eldsteMs ?? naaMs)) / 1000));
    const alder = s < 60 ? `${s} s` : `${Math.floor(s / 60)} min`;
    deler.push(
      `${st.ventende} melding${st.ventende === 1 ? '' : 'er'} venter på samband `
      + `(eldste ${alder}) — ingenting er tapt, de går ut når linja er tilbake`,
    );
  }
  if (st.overlop) {
    // ⚠ ALDRI STILLE. Et overløp er tapte meldinger, og operatøren må vite at
    // hun må sende dem på nytt — på radio om nødvendig.
    deler.push(`⚠ ${st.overlop} melding${st.overlop === 1 ? '' : 'er'} MISTET (køen ble full) — send dem på nytt`);
  }
  return deler.join(' · ');
}

/**
 * `localStorage` pakket som `KoLager`, eller `null` når det ikke finnes.
 *
 * ⚠ RETURNERER null FRAMFOR Å KASTE. Privat nettlesermodus og noen kiosk-oppsett
 * gjør `localStorage` utilgjengelig, og da skal køen fortsatt virke — bare uten å
 * overleve en sidelasting. Å velte oppstarten fordi holdbarheten mangler ville
 * gjort en liten mangel til total svikt.
 */
export function nettlagring(): KoLager | null {
  try {
    const l = globalThis.localStorage;
    if (!l) return null;
    return {
      hent: (n) => l.getItem(n),
      sett: (n, v) => l.setItem(n, v),
    };
  } catch { return null; }
}
